import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type Selection, useToken } from "@trydig/centra/client";
import { tryCatch } from "@trydig/utils";
import { ofetch } from "ofetch";
import styles from "~/components/ui/AddToCartButton/AddToCartButton.module.css";
import { selectors, useMicrocartStore } from "~/features/cart/MicroCart/MicroCartContext";
import { gtm } from "~/lib/gtm";
import { selectionItemToGTMProduct } from "~/lib/gtm/utils";
import { useCurrency } from "~/lib/hooks/useCentra";
import { useModal } from "~/lib/hooks/useModal";
import type { MicroCartItem } from "~/lib/stateManagement/sessionState/sessionSlices/microCart";

// const apiUrl = process.env.NEXT_PUBLIC_CENTRA_CHECKOUT;

const addAllToCart = async (
	variables: {
		items: {
			id: string;
			quantity: number;
			comment?: string;
		}[];
	},
	token: string,
) => {
	const [response, error] = await tryCatch(
		ofetch("/api/microcart", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Api-Token": token,
			},
			body: JSON.stringify(variables),
		}),
	);

	if (error) {
		throw new Error("Error adding to cart");
	}

	return response;
};

const useAddToCart = () => {
	const queryClient = useQueryClient();
	if (!queryClient) console.error("No queryClient");

	const customerToken = useToken();
	const { openModal } = useModal("cart", () => {}, true);

	const addToCart = useMutation({
		mutationFn: (variables: {
			items: {
				id: string;
				quantity: number;
			}[];
		}) => addAllToCart(variables, customerToken || ""),

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["selection"],
			});

			openModal();
		},

		onError: () => {
			queryClient.invalidateQueries({
				queryKey: ["selection"],
			});
		},
	});
	return { addToCart };
};

export type AddToCart = ReturnType<typeof useAddToCart>["addToCart"];

interface AddToCartMicroCartProps {
	soldOut?: boolean;
}

export const AddToCartMicroCart = ({ soldOut }: AddToCartMicroCartProps) => {
	const totalPrice = useMicrocartStore(selectors.totalPrice);
	const carts = useMicrocartStore(selectors.cartsArray);
	const resetCarts = useMicrocartStore((state) => state.resetMicroCart);
	const { addToCart } = useAddToCart();
	const { data: currency } = useCurrency();

	const allItems = carts
		?.reduce((acc: MicroCartItem[], cart) => {
			if (!cart?.items) return acc;
			return [...acc, ...cart.items];
		}, [])
		.filter((i) => i.quantity > 0);

	const allItemsOutOfStock = allItems?.every((item) => !item.stock || item.stock === "no");

	const handleAddToCart = () => {
		addToCart.mutate(
			{
				items: allItems.map((item: any) => ({
					id: item.id,
					quantity: item.quantity,
					comment: item?.comment,
				})),
			},
			{
				onSuccess: (selection: Selection, { items }) => {
					if (!selection?.selection?.currency) return;

					for (const item of items) {
						const selectionItem = selection?.selection?.items?.find((i) => i.item === item.id);

						if (!selectionItem) continue;

						const ga4Item = selectionItemToGTMProduct({
							item: selectionItem,
							currency: selection.selection.currency,
						});

						gtm.addToCart(ga4Item);
					}
				},
			},
		);
		resetCarts();
	};

	return (
		<button
			disabled={addToCart.isPending || allItemsOutOfStock || soldOut}
			type="button"
			title={allItemsOutOfStock ? "Ikke på lager" : undefined}
			className={styles["add-to-cart"]}
			onClick={handleAddToCart}
		>
			{!allItemsOutOfStock && !soldOut ? "Legg i handlekurv" : "Midlertidig utsolgt"}

			{!soldOut && (
				<span>
					{currency} {totalPrice.toFixed(2)}
				</span>
			)}
		</button>
	);
};
